import { createContext, useContext, useState, ReactNode } from 'react';

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
}

interface UsersContextValue {
  users: User[];
  setUsers: (users: User[]) => void;
  selectedUser: User | null;
  setSelectedUser: (user: User | null) => void;
  updateUserRole: (userId: string, newRole: string) => void;
}

const UsersContext = createContext<UsersContextValue | undefined>(undefined);

interface UsersProviderProps {
  children: ReactNode;
}

export function UsersProvider({ children }: UsersProviderProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const updateUserRole = (userId: string, newRole: string) => {
    // Update the user in the users list
    setUsers(prevUsers =>
      prevUsers.map(user =>
        user.id === userId ? { ...user, role: newRole } : user
      )
    );

    // Update selected user if it's the same user
    setSelectedUser(prevSelected =>
      prevSelected?.id === userId
        ? { ...prevSelected, role: newRole }
        : prevSelected
    );
  };

  const value = {
    users,
    setUsers,
    selectedUser,
    setSelectedUser,
    updateUserRole,
  };

  return <UsersContext.Provider value={value}>{children}</UsersContext.Provider>;
}

export function useUsersContext(): UsersContextValue {
  const context = useContext(UsersContext);
  if (context === undefined) {
    throw new Error('useUsersContext must be used within a UsersProvider');
  }
  return context;
}

